"use client";
import React, { useEffect, useRef, useState } from "react";

interface StarsBackgroundProps {
  starDensity?: number;
  allStarsTwinkle?: boolean;
  twinkleProbability?: number;
  minTwinkleSpeed?: number;
  maxTwinkleSpeed?: number;
  className?: string;
}

export const StarsBackground: React.FC<StarsBackgroundProps> = ({
  starDensity = 0.00015,
  allStarsTwinkle = true,
  twinkleProbability = 0.7,
  minTwinkleSpeed = 0.5,
  maxTwinkleSpeed = 1,
  className,
}) => {
  const [stars, setStars] = useState<
    {
      x: number;
      y: number;
      radius: number;
      opacity: number;
      twinkleSpeed: number | null;
    }[]
  >([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const updateStars = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Use window dimensions to ensure full coverage
        const width = window.innerWidth;
        const height = window.innerHeight;
        canvas.width = width;
        canvas.height = height;

        const starCount = Math.floor(width * height * starDensity);
        const newStars = Array.from({ length: starCount }, () => {
          const shouldTwinkle =
            allStarsTwinkle || Math.random() < twinkleProbability;
          return {
            x: Math.random() * width,
            y: Math.random() * height,
            radius: Math.random() * 1.5 + 0.5,
            opacity: Math.random() * 0.5 + 0.5,
            twinkleSpeed: shouldTwinkle
              ? minTwinkleSpeed +
                Math.random() * (maxTwinkleSpeed - minTwinkleSpeed)
              : null,
          };
        });
        setStars(newStars);
      }
    };

    updateStars();

    const handleResize = () => {
      updateStars();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    starDensity,
    allStarsTwinkle,
    twinkleProbability,
    minTwinkleSpeed,
    maxTwinkleSpeed,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;

    const render = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((star) => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;

        if (star.twinkleSpeed !== null) {
          const twinkle =
            (Math.sin(time * 0.001 * star.twinkleSpeed) + 1) * 0.5;
          ctx.globalAlpha = star.opacity * twinkle;
        } else {
          ctx.globalAlpha = star.opacity;
        }

        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [stars]);

  return (
    <canvas
      ref={canvasRef}
      className={`w-full h-full absolute inset-0 ${className}`}
      style={{ pointerEvents: "none" }}
    />
  );
};
